import { useUnmount } from 'ahooks';
import { useContext, useRef } from 'react';
import { TextLayerExtension } from '@meitu/saas-editor-core/es/editor/layer-extensions/text-layer';
import { TextEditPlugin } from '@meitu/saas-editor-core/es/plugins/text-edit';
import { type ITextLayerProps } from '@meitu/saas-editor-core/es/version-types';
import { FontSizeSelector } from '@/components/font-size-selector';
import { useEditor } from '@/hooks/use-editor';
import { useGlobalWidget } from '@/hooks/use-global-widget';
import { checkIsInArtTextGeneratorMode } from '@/utils/mode';
import { TextPanelContext } from '../../context';
import { useEditingRange, useLayerProps, useTextContent } from '../../hooks';
import { Tracker } from '../../utils';

const FALLBACK_FONT_SIZE = 6;

export const FontSize: React.FC = () => {
    const editor = useEditor();
    const { layerId, recordId } = useContext(TextPanelContext)!;

    const props = useLayerProps(editor, (layer) => {
        if (!layer) return null;

        const layerData = layer.data as ITextLayerProps;
        return {
            fontSize: layerData.fontSize,
            isOnTextEditing: layer.__isOnTextEditing || false,
            advancedTextMaterial: layerData.advancedTextMaterial,
        };
    });

    const fontSize = props?.fontSize;
    const isOnTextEditing = props?.isOnTextEditing || false;
    const advancedTextMaterial = props?.advancedTextMaterial;

    const ignoreRange = !checkIsInArtTextGeneratorMode() && !!advancedTextMaterial;

    const editingRange = useEditingRange(editor);
    const textContent = useTextContent(editor, editingRange);

    const popoverWidgetRef = useGlobalWidget(editor, false);

    const finalFontSize = (() => {
        if (textContent) return textContent.fontSize;
        return fontSize ? fontSize : FALLBACK_FONT_SIZE;
    })();

    const unlockFn = useRef<(() => void) | undefined>(undefined);

    const handleOpenChange = (open: boolean) => {
        popoverWidgetRef.current = open;
        if (open) {
            unlockFn.current = editor.getInstance(TextEditPlugin).lockEditingRange();
        } else {
            if (unlockFn.current) {
                unlockFn.current();
                unlockFn.current = undefined;
            }
        }
    };

    const handleChange = (value: number, options = { writeHistory: false }) => {
        editor.getInstance(TextLayerExtension).setFontSize(layerId, value, { ...options, ignoreRange });

        if (options.writeHistory) {
            Tracker.setFontSize({
                recordId,
                stageType: editor.stage.getType(),
                fontSize: value,
            });
        }
    };

    useUnmount(() => {
        if (unlockFn.current) {
            unlockFn.current();
            unlockFn.current = undefined;
        }
    });

    return (
        <FontSizeSelector
            value={finalFontSize}
            disableMouseWheel={isOnTextEditing}
            onChange={(value) => handleChange(value, { writeHistory: false })}
            onAfterChange={(value) => handleChange(value, { writeHistory: true })}
            onOpenChange={handleOpenChange}
        />
    );
};
